import {Trans, useTranslation} from "react-i18next";
import {css, Theme, useTheme} from "@emotion/react";
import {elevation} from "@pg-design/elevation";
import {FormikForm} from "@pg-design/formik-utils";
import {Col, Container, Row} from "@pg-design/grid";
import {
    borderRadius,
    calculateRemSize,
    flexAbsoluteCenter,
    mb,
    mt,
    onDesktop,
    p,
    pv,
    w100
} from "@pg-design/helpers-css";
import {BrandCheckboxCheckedIcon} from "@pg-design/icons";
import {Link} from "@pg-design/link";
import {Modal} from "@pg-design/modal";
import {Text} from "@pg-design/text";
import {Form, Formik, FormikHelpers} from "formik";
import * as Yup from "yup";

import {useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {IPGEmployee, pgEmployees} from "../../../common/app/constants/pg_employees";
import {chartsRoutes} from "../../../common/app/routing/charts_routes";
import {ContactPerson} from "../../../components/contact/ContactPerson";
import {handleFormikSubmit} from "../../../components/form/handle_submit";
import {RodoTerms} from "../../../components/RodoTerms";
import {SubmitFormikButton} from "../../../components/SubmitFormikButton";
import {selectAuthStatus} from "../../auth/auth_slice";
import {DataProtectionNotice} from "../../auth/components/auth_modal/DataProtectionNotice";
import {selectUserData} from "../../users/users_slice";
import {useContactFormInquiryMutation} from "../api/add_contact_form_inquiry";
import {buildContactInquiryPayload, ILandingContactFormValues} from "./landing_contact_form.utils";

const landingContactPerson: IPGEmployee = {
    ...pgEmployees.aleksandra_boratynska,
    role: "landing.bigDataContact.person_role",
    email: "bigdata@rynekpierwotny.pl"
};

const BigDataLandingContactForm = () => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const [addContactFormInquiry, {isSuccess, reset}] = useContactFormInquiryMutation({
        fixedCacheKey: "landingBigDataContactInquiry"
    });
    const {isLoggedIn} = useAppSelector(selectAuthStatus);
    const userData = useAppSelector(selectUserData);
    const hasData = isLoggedIn && !!userData;
    const routes = chartsRoutes[i18n.language] ?? chartsRoutes.pl;
    const showMarketingConsent = !isLoggedIn || (hasData && !userData.bigdata_marketing_consent);

    const initialValues: ILandingContactFormValues = {
        full_name: "",
        email: "",
        phone: "",
        message: "",
        terms_accepted: false,
        bigdata_marketing_consent: hasData ? userData.bigdata_marketing_consent : false
    };

    const validationSchema = Yup.object({
        full_name: Yup.string()
            .trim()
            .required(t("common.form.required_field"))
            .min(3, t("common.form.required_field")),
        email: Yup.string().required(t("common.form.required_field")).email(t("common.form.invalid_email")),
        phone: Yup.string()
            .required(t("common.form.required_field"))
            .transform((v) => (v || "").replace(/\D/g, ""))
            .test("nine", t("common.form.phone_length"), (v) => typeof v === "string" && v.length === 9),
        message: Yup.string().trim().required(t("common.form.required_field")),
        terms_accepted: Yup.boolean().oneOf([true], t("common.form.required_field")),
        bigdata_marketing_consent: Yup.boolean()
    });

    const onSubmit = (values: ILandingContactFormValues, helpers: FormikHelpers<ILandingContactFormValues>) => {
        return handleFormikSubmit(
            addContactFormInquiry(buildContactInquiryPayload(values, showMarketingConsent)).unwrap(),
            helpers,
            {
                onSuccess: () => {
                    helpers.setSubmitting(false);
                    helpers.resetForm();
                }
            }
        );
    };

    return (
        <div css={formPanel}>
            <Modal isOpen={isSuccess} onModalClose={() => reset()}>
                <Modal.Content>
                    <div css={successModal}>
                        <div css={flexAbsoluteCenter}>
                            <BrandCheckboxCheckedIcon
                                css={mb(3)}
                                size="6.4"
                                wrapperColor={theme.colors.primary}
                                wrapperSize="6.4"
                            />
                        </div>
                        <Text variant="headline_6" align="center" css={mb(1)}>
                            {t("landing.bigDataContact.form.success_title")}
                        </Text>
                        <Text variant="info_txt_1" align="center">
                            {t("landing.bigDataContact.form.success_lead")}
                        </Text>
                    </div>
                </Modal.Content>
            </Modal>

            <Text variant="headline_6" css={mb(0)}>
                {t("landing.bigDataContact.form.title")}
            </Text>
            <Text variant="info_txt_1" color={theme.colors.gray[700]} css={mb(2)}>
                {t("landing.bigDataContact.form.lead")}
            </Text>

            <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
                {({handleBlur, isSubmitting, isValid}) => (
                    <Form>
                        <FormikForm.Input
                            onBlur={handleBlur}
                            css={field}
                            name="full_name"
                            placeholder={t("landing.bigDataContact.form.name_placeholder")}
                        />
                        <FormikForm.Input
                            onBlur={handleBlur}
                            css={field}
                            type="email"
                            name="email"
                            placeholder={t("landing.bigDataContact.form.email_placeholder")}
                        />
                        <FormikForm.Input
                            onBlur={handleBlur}
                            css={field}
                            name="phone"
                            placeholder={t("landing.bigDataContact.form.phone_placeholder")}
                        />
                        <FormikForm.Textarea
                            onBlur={handleBlur}
                            css={[field, messageField]}
                            name="message"
                            placeholder={t("landing.bigDataContact.form.message_placeholder")}
                            rows={4}
                        />

                        <Text variant="info_txt_2" color={theme.colors.gray[600]} css={requiredNote}>
                            * {t("landing.bigDataContact.form.required_note")}
                        </Text>

                        <FormikForm.Checkbox
                            onBlur={handleBlur}
                            css={checkbox}
                            name="terms_accepted"
                            labelContent={
                                <Text variant="info_txt_1">
                                    <Trans
                                        t={t}
                                        i18nKey="landing.bigDataContact.form.terms_consent"
                                        components={[
                                            <Link
                                                key="terms"
                                                href={routes.terms}
                                                target="_blank"
                                                underline
                                                size="inherit"
                                            >
                                                {null}
                                            </Link>,
                                            <Link
                                                key="privacy"
                                                href={routes.privacyPolicy}
                                                target="_blank"
                                                underline
                                                size="inherit"
                                            >
                                                {null}
                                            </Link>
                                        ]}
                                    />{" "}
                                    *
                                </Text>
                            }
                            id="landing-bd-contact-terms"
                            checkedMarkColor="var(--green-checkbox-checked)"
                        />

                        {showMarketingConsent && (
                            <FormikForm.Checkbox
                                onBlur={handleBlur}
                                css={checkbox}
                                name="bigdata_marketing_consent"
                                labelContent={
                                    <Text variant="info_txt_1">
                                        {t("landing.bigDataContact.form.marketing_consent")}
                                    </Text>
                                }
                                id="landing-bd-contact-marketing"
                                checkedMarkColor="var(--green-checkbox-checked)"
                            />
                        )}

                        <SubmitFormikButton
                            css={submitBtn}
                            disabled={isSubmitting || !isValid}
                            copy={t("landing.bigDataContact.form.submit")}
                        />

                        <div css={notice}>
                            <DataProtectionNotice />
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

interface IBigDataLandingContactProps {
    id?: string;
}

export const BigDataLandingContact = ({id}: IBigDataLandingContactProps) => {
    const {t} = useTranslation();
    const theme = useTheme();

    return (
        <Container as="section" fluid css={section} id={id}>
            <Container>
                <Row>
                    <Col mdOffset={1} md={10} lgOffset={2} lg={12} sm={4}>
                        <Text variant="headline_1" align="center" as="h2" css={mb(6)}>
                            {t("landing.bigDataContact.section_title")}
                        </Text>
                    </Col>
                </Row>

                <Row css={contactRow(theme)}>
                    <Col lgOffset={3} lg={5} mdOffset={1} md={5} sm={4} css={column}>
                        <Text css={mb(0)} variant="headline_6">
                            {t("landing.bigDataContact.lead_title")}
                        </Text>
                        <Text variant="info_txt_1" color={theme.colors.gray[800]} css={mb(2)}>
                            {t("landing.bigDataContact.lead_text")}
                        </Text>
                        <ContactPerson {...landingContactPerson} contentAlign="left" />
                        <div css={mt(1)}>
                            <RodoTerms align="left" />
                        </div>
                    </Col>

                    <Col lgOffset={1} lg={5} md={5} sm={4} css={column}>
                        <BigDataLandingContactForm />
                    </Col>
                </Row>
            </Container>
        </Container>
    );
};

const section = css`
    ${pv(9)};
    background-color: #fff;
    width: 100%;
`;

const contactRow = (theme: Theme) => css`
    align-items: flex-start;
    row-gap: ${calculateRemSize(5)};

    ${onDesktop(css`
        row-gap: 0;
    `)}

    @media (max-width: ${theme.breakpoints.numeric.md - 1}px) {
        flex-direction: column;
    }
`;

const column = css`
    display: flex;
    flex-direction: column;
    min-width: 0;
    ${w100};
`;

const formPanel = css`
    ${w100};
    ${p(2)};
    box-sizing: border-box;
    background-color: #fff;
    ${borderRadius(2)};
    ${elevation(4)};
`;

const field = css`
    ${mb(1)};
    ${w100};
`;

const messageField = css`
    min-height: 7rem;
    resize: vertical;
`;

const requiredNote = css`
    display: block;
    ${mb(2)};
`;

const checkbox = css`
    ${mb(2)};
`;

const submitBtn = css`
    ${w100};
    ${mb(3)};
    margin-top: ${calculateRemSize(1)};
    text-transform: uppercase;
    font-weight: 700;
`;

const notice = css`
    display: block;
`;

const successModal = css`
    ${pv(6)};
    ${p(4)};

    ${onDesktop(css`
        width: var(--modal-width-small);
    `)};
`;
